/**
 * burst-kinetics.ts
 *
 * Explicit teaching illustration for Roadmap #33.
 * Fits hand-entered example curves with a sigmoid approximation and Nelder-Mead.
 * The fit does not establish biological parameters or uncertainty. Annotation
 * keyword matches feed illustrative cassette scenarios, not measured timing.
 * Validated experimental provenance and an identifiable forward model remain
 * required for the planned biological inference capability.
 */

import type { PhageFull } from '../types';

export type MeasurementType = 'OD' | 'PFU' | 'CFU';

export interface DataPoint {
  timeMin: number;
  value: number;
  type: MeasurementType;
  uncertainty?: number;
}

export interface InfectionParameters {
  adsorptionRate: number;      // k: mL/(phage·min) (e.g. 1e-10 to 5e-9)
  latentPeriod: number;        // L: minutes (e.g. 15 to 90 min)
  burstSize: number;           // b: virions released per infected cell (e.g. 20 to 300)
  bacterialGrowthRate: number; // mu: min^-1 (e.g. 0.01 to 0.03 min^-1 ≈ 0.6 to 1.8 hr^-1)
  phageDecayRate: number;      // delta: min^-1 (e.g. 0.0001 to 0.005 min^-1)
  initialBacteria: number;     // B0: cells/mL (e.g. 1e7 to 5e8)
  initialPhage: number;        // P0: PFU/mL (e.g. 1e6 to 1e8)
}

export interface ConfidenceIntervals {
  adsorptionRate: [number, number];
  latentPeriod: [number, number];
  burstSize: [number, number];
  bacterialGrowthRate: [number, number];
}

export interface TrajectoryPoint {
  timeMin: number;
  bacteria: number;
  infected: number;
  phage: number;
  totalBiomass: number; // B + I
  od600: number;        // (B + I) / conversionFactor
}

export interface ResidualPoint {
  timeMin: number;
  observed: number;
  predicted: number;
  residual: number;
}

export interface ExperimentalGrowthCurve {
  id: string;
  title: string;
  phageName: string;
  hostName: string;
  citation: string;
  moi: number;
  defaultB0: number;
  defaultP0: number;
  data: DataPoint[];
}

export type LysisGeneRole = 'holin' | 'antiholin' | 'endolysin' | 'spanin' | 'accessory';

export interface LysisCassetteGene {
  role: LysisGeneRole;
  geneId: number;
  name: string;
  product: string;
  locusTag?: string | null;
  mechanism: string;
  transmembraneHelices?: number;
}

export interface LysisCassetteAnalysis {
  genes: LysisCassetteGene[];
  hasHolin: boolean;
  hasAntiholin: boolean;
  hasEndolysin: boolean;
  hasSpanin: boolean;
  predictedLysisTimingMin: number;
  timingModulators: string[];
  architectureSummary: string;
}

export interface GenomicCorrelationResult {
  observedVsPredictedDeltaMin: number;
  concordance: 'high' | 'moderate' | 'divergent';
  correlationScore: number; // 0..100
  insights: string[];
}

export interface InSilicoMutationShift {
  mutationType: 'antiholin_knockout' | 'holin_overexpression' | 'endolysin_class_swap' | 'delayed_lysis';
  description: string;
  predictedLatentPeriodMin: number;
  predictedBurstSize: number;
  latentPeriodDeltaMin: number;
  burstSizeDelta: number;
  mechanisticRationale: string;
}

export interface BurstInferenceResult {
  source: 'demonstration';
  assumptions: string;
  adsorptionRateStatus: 'unidentifiable' | 'unvalidated_fit';
  curveId: string;
  curveTitle: string;
  phageName: string;
  fittedParameters: InfectionParameters;
  confidenceIntervals: null;
  fitQualityR2: number | null;
  logLikelihood: number;
  aic: number;
  bic: number;
  residuals: ResidualPoint[];
  fittedTrajectory: TrajectoryPoint[];
  lysisCassette: LysisCassetteAnalysis;
  genomicCorrelation: GenomicCorrelationResult;
  inSilicoScenarios: InSilicoMutationShift[];
  summary: string;
}

// Typical OD600 to cells/mL conversion factor for E. coli / rod-shaped bacteria
export const OD600_CELLS_PER_ML = 8.0e8;

/**
 * Benchmark experimental growth curves from phage literature
 */
export const CANONICAL_GROWTH_CURVES: Record<string, ExperimentalGrowthCurve> = {
  t4_ecoli: {
    id: 't4_ecoli',
    title: 'Enterobacteria phage T4 One-Step Growth (Ellis & Delbrück 1939)',
    phageName: 'Enterobacteria phage T4',
    hostName: 'Escherichia coli B',
    citation: 'Ellis EL, Delbrück M. The growth of bacteriophage. J Gen Physiol. 1939;22(3):365-384.',
    moi: 0.1,
    defaultB0: 1.0e8,
    defaultP0: 1.0e7,
    data: [
      { timeMin: 0, value: 1.0e7, type: 'PFU' },
      { timeMin: 5, value: 9.8e6, type: 'PFU' },
      { timeMin: 10, value: 9.5e6, type: 'PFU' },
      { timeMin: 15, value: 9.6e6, type: 'PFU' },
      { timeMin: 20, value: 1.05e7, type: 'PFU' },
      { timeMin: 23, value: 1.4e7, type: 'PFU' },
      { timeMin: 25, value: 3.2e7, type: 'PFU' },
      { timeMin: 28, value: 1.8e8, type: 'PFU' },
      { timeMin: 32, value: 6.5e8, type: 'PFU' },
      { timeMin: 36, value: 1.2e9, type: 'PFU' },
      { timeMin: 40, value: 1.35e9, type: 'PFU' },
      { timeMin: 50, value: 1.38e9, type: 'PFU' },
    ],
  },
  lambda_ecoli: {
    id: 'lambda_ecoli',
    title: 'Escherichia phage Lambda Latent Period & Lysis (Wang 2000)',
    phageName: 'Enterobacteria phage lambda',
    hostName: 'Escherichia coli K-12',
    citation: 'Wang IN, Smith DL, Young R. Holins: the protein clocks of bacteriophage infections. Annu Rev Microbiol. 2000;54:799-825.',
    moi: 0.2,
    defaultB0: 2.0e8,
    defaultP0: 4.0e7,
    data: [
      { timeMin: 0, value: 4.0e7, type: 'PFU' },
      { timeMin: 10, value: 3.8e7, type: 'PFU' },
      { timeMin: 20, value: 3.7e7, type: 'PFU' },
      { timeMin: 30, value: 3.9e7, type: 'PFU' },
      { timeMin: 40, value: 4.2e7, type: 'PFU' },
      { timeMin: 45, value: 7.5e7, type: 'PFU' },
      { timeMin: 48, value: 3.1e8, type: 'PFU' },
      { timeMin: 52, value: 1.5e9, type: 'PFU' },
      { timeMin: 58, value: 3.2e9, type: 'PFU' },
      { timeMin: 65, value: 3.6e9, type: 'PFU' },
      { timeMin: 75, value: 3.65e9, type: 'PFU' },
    ],
  },
  phix174_ecoli: {
    id: 'phix174_ecoli',
    title: 'Bacteriophage phiX174 Lysis Kinetics (Hutchison & Sinsheimer 1966)',
    phageName: 'Escherichia virus phiX174',
    hostName: 'Escherichia coli C',
    citation: 'Hutchison CA, Sinsheimer RL. The process of infection with bacteriophage phi-X174. X. Lysis of infected cells. J Mol Biol. 1966;18(3):429-447.',
    moi: 0.15,
    defaultB0: 1.5e8,
    defaultP0: 2.2e7,
    data: [
      { timeMin: 0, value: 2.2e7, type: 'PFU' },
      { timeMin: 5, value: 2.1e7, type: 'PFU' },
      { timeMin: 10, value: 2.0e7, type: 'PFU' },
      { timeMin: 15, value: 2.3e7, type: 'PFU' },
      { timeMin: 18, value: 4.8e7, type: 'PFU' },
      { timeMin: 21, value: 2.5e8, type: 'PFU' },
      { timeMin: 25, value: 1.8e9, type: 'PFU' },
      { timeMin: 30, value: 3.7e9, type: 'PFU' },
      { timeMin: 40, value: 3.9e9, type: 'PFU' },
    ],
  },
  pseudomonas_pak_p1: {
    id: 'pseudomonas_pak_p1',
    title: 'Pseudomonas phage PAK_P1 OD600 Clearance (Henry et al. 2013)',
    phageName: 'Pseudomonas phage PAK_P1',
    hostName: 'Pseudomonas aeruginosa PAK',
    citation: 'Henry M, et al. In vitro and in vivo assessment of phage therapy against Pseudomonas aeruginosa. J Antimicrob Chemother. 2013;68(7):1552-1560.',
    moi: 0.5,
    defaultB0: 4.0e8, // OD600 ~ 0.50
    defaultP0: 2.0e8,
    data: [
      { timeMin: 0, value: 0.50, type: 'OD' },
      { timeMin: 10, value: 0.55, type: 'OD' },
      { timeMin: 20, value: 0.62, type: 'OD' },
      { timeMin: 30, value: 0.68, type: 'OD' },
      { timeMin: 35, value: 0.65, type: 'OD' },
      { timeMin: 40, value: 0.52, type: 'OD' },
      { timeMin: 45, value: 0.35, type: 'OD' },
      { timeMin: 50, value: 0.22, type: 'OD' },
      { timeMin: 60, value: 0.14, type: 'OD' },
      { timeMin: 75, value: 0.08, type: 'OD' },
      { timeMin: 90, value: 0.06, type: 'OD' },
    ],
  },
};

/**
 * 4th-Order Runge-Kutta (RK4) ODE numerical integrator
 */
export function rungeKutta4(
  derivatives: (t: number, y: number[]) => number[],
  y0: number[],
  tSpan: [number, number],
  dt: number
): Array<{ t: number; y: number[] }> {
  const result: Array<{ t: number; y: number[] }> = [];
  let t = tSpan[0];
  let y = [...y0];

  const tEnd = tSpan[1];
  const maxSteps = 10000;
  let steps = 0;

  while (t <= tEnd && steps < maxSteps) {
    result.push({ t, y: [...y] });

    const k1 = derivatives(t, y);
    const k2 = derivatives(t + dt * 0.5, y.map((yi, i) => Math.max(0, yi + dt * 0.5 * k1[i])));
    const k3 = derivatives(t + dt * 0.5, y.map((yi, i) => Math.max(0, yi + dt * 0.5 * k2[i])));
    const k4 = derivatives(t + dt, y.map((yi, i) => Math.max(0, yi + dt * k3[i])));

    y = y.map((yi, i) => Math.max(0, yi + (dt / 6.0) * (k1[i] + 2 * k2[i] + 2 * k3[i] + k4[i])));
    t += dt;
    steps++;
  }

  return result;
}

/**
 * Infection system differential equations:
 * dB/dt = mu * B - k * B * P
 * dI/dt = k * B * P - (1 / L) * I
 * dP/dt = (b / L) * I - k * B * P - delta * P
 */
export function infectionODE(params: InfectionParameters): (t: number, y: number[]) => number[] {
  const {
    adsorptionRate: k,
    latentPeriod: L,
    burstSize: b,
    bacterialGrowthRate: mu,
    phageDecayRate: delta,
  } = params;

  const safeL = Math.max(1.0, L);

  return (_t: number, y: number[]) => {
    const [B, I, P] = y;
    const adsorptionFlux = k * B * P;
    const lysisFlux = I / safeL;

    const dB = mu * B - adsorptionFlux;
    const dI = adsorptionFlux - lysisFlux;
    const dP = b * lysisFlux - adsorptionFlux - delta * P;

    return [dB, dI, dP];
  };
}

/**
 * Error function approximation (Abramowitz and Stegun 7.1.26)
 */
export function erf(x: number): number {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x);
  const t = 1.0 / (1.0 + p * absX);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
  return sign * y;
}

/**
 * Standard Normal Cumulative Distribution Function
 */
export function normalCdf(x: number, mean: number, std: number): number {
  const safeStd = Math.max(0.1, std);
  return 0.5 * (1.0 + erf((x - mean) / (safeStd * Math.SQRT2)));
}

/**
 * Estimate initial parameter values directly from time series features
 */
export function estimateInitialParameters(
  data: DataPoint[],
  defaultB0: number = 1.0e8,
  defaultP0: number = 1.0e7
): InfectionParameters {
  if (data.length === 0) {
    return {
      adsorptionRate: 2.0e-10,
      latentPeriod: 30.0,
      burstSize: 100.0,
      bacterialGrowthRate: 0.015,
      phageDecayRate: 0.0005,
      initialBacteria: defaultB0,
      initialPhage: defaultP0,
    };
  }

  const isOD = data[0].type === 'OD';
  if (isOD) {
    let maxIdx = 0;
    for (let i = 1; i < data.length; i++) {
      if (data[i].value > data[maxIdx].value) {
        maxIdx = i;
      }
    }
    const peakTime = data[maxIdx].timeMin;
    const latentPeriod = Math.max(12, peakTime);
    const mu = peakTime > 0 ? Math.max(0.005, Math.log(data[maxIdx].value / Math.max(0.01, data[0].value)) / peakTime) : 0.015;

    return {
      adsorptionRate: 2.5e-10,
      latentPeriod,
      burstSize: 85.0,
      bacterialGrowthRate: Math.round(mu * 1000) / 1000,
      phageDecayRate: 0.0005,
      initialBacteria: defaultB0,
      initialPhage: defaultP0,
    };
  } else {
    const p0 = data[0].value;
    let takeoffTime = 25.0;
    for (let i = 1; i < data.length; i++) {
      if (data[i].value > p0 * 1.5) {
        takeoffTime = data[i - 1].timeMin + (data[i].timeMin - data[i - 1].timeMin) * 0.4;
        break;
      }
    }

    const maxP = Math.max(...data.map((d) => d.value));
    const burstSize = Math.max(10, Math.min(500, Math.round(maxP / Math.max(1, p0))));

    return {
      adsorptionRate: 2.0e-10,
      latentPeriod: Math.round(takeoffTime * 10) / 10,
      burstSize,
      bacterialGrowthRate: 0.015,
      phageDecayRate: 0.0005,
      initialBacteria: defaultB0,
      initialPhage: p0,
    };
  }
}

/**
 * Simulate continuous infection trajectory matching delay-differential / one-step kinetics
 */
export function simulateInfectionTrajectory(
  params: InfectionParameters,
  tMaxMin: number,
  dtMin: number = 0.5
): TrajectoryPoint[] {
  const points: TrajectoryPoint[] = [];
  const sigma = Math.max(1.2, params.latentPeriod * 0.12);
  const b0 = params.initialBacteria;
  const p0 = params.initialPhage;

  for (let t = 0; t <= tMaxMin; t += dtMin) {
    // Sigmoidal lysis transition: inflection at L + 1.2 * sigma, width sigma
    const lysisFrac = normalCdf(t, params.latentPeriod + 1.2 * sigma, sigma);

    // Free phage count
    const burstPhage = p0 * (params.burstSize - 1.0) * lysisFrac;
    const currentPhage = Math.max(0, (p0 + burstPhage) * Math.exp(-params.phageDecayRate * t));

    // Bacterial biomass
    const growth = Math.exp(params.bacterialGrowthRate * t);
    const unlysedFrac = Math.max(0.06, 1.0 - 0.94 * lysisFrac);
    const currentBacteria = b0 * growth * unlysedFrac;
    const infectedCells = b0 * Math.max(0, (1.0 - lysisFrac) * (1.0 - Math.exp(-params.adsorptionRate * p0 * Math.min(t, params.latentPeriod))));

    const totalBiomass = currentBacteria + infectedCells;
    const od600 = Math.round((totalBiomass / OD600_CELLS_PER_ML) * 1000) / 1000;

    points.push({
      timeMin: Math.round(t * 100) / 100,
      bacteria: Math.round(currentBacteria),
      infected: Math.round(infectedCells),
      phage: Math.round(currentPhage),
      totalBiomass: Math.round(totalBiomass),
      od600,
    });
  }

  return points;
}

/**
 * Interpolate model prediction at a specific experimental time
 */
export function predictAtTime(
  trajectory: TrajectoryPoint[],
  timeMin: number,
  type: MeasurementType
): number {
  if (trajectory.length === 0) return 0;
  if (timeMin <= trajectory[0].timeMin) {
    return type === 'OD' ? trajectory[0].od600 : type === 'PFU' ? trajectory[0].phage : trajectory[0].bacteria;
  }
  const last = trajectory[trajectory.length - 1];
  if (timeMin >= last.timeMin) {
    return type === 'OD' ? last.od600 : type === 'PFU' ? last.phage : last.bacteria;
  }

  let idx = 0;
  while (idx < trajectory.length - 1 && trajectory[idx + 1].timeMin < timeMin) {
    idx++;
  }

  const p0 = trajectory[idx];
  const p1 = trajectory[idx + 1];
  const alpha = (timeMin - p0.timeMin) / (p1.timeMin - p0.timeMin);

  if (type === 'OD') {
    return p0.od600 + alpha * (p1.od600 - p0.od600);
  } else if (type === 'PFU') {
    return p0.phage + alpha * (p1.phage - p0.phage);
  } else {
    return p0.bacteria + alpha * (p1.bacteria - p0.bacteria);
  }
}

/**
 * Calculate log-likelihood and sum-of-squared errors for a dataset
 */
export function evaluateFit(
  params: InfectionParameters,
  data: DataPoint[],
  tMaxMin?: number
): { sse: number; logLikelihood: number; residuals: ResidualPoint[]; trajectory: TrajectoryPoint[] } {
  const maxDataTime = Math.max(...data.map((d) => d.timeMin));
  const tMax = Math.max(maxDataTime * 1.05, tMaxMin ?? maxDataTime);
  const trajectory = simulateInfectionTrajectory(params, tMax, 0.5);

  let sse = 0;
  let logLikelihood = 0;
  const residuals: ResidualPoint[] = [];

  const sigmaOD = 0.04;
  const sigmaLogPFU = 0.25;

  for (const point of data) {
    const predicted = predictAtTime(trajectory, point.timeMin, point.type);
    let diff = 0;
    let sigma = sigmaOD;

    if (point.type === 'OD') {
      diff = point.value - predicted;
      sigma = sigmaOD;
    } else {
      const obsLog = Math.log10(Math.max(1, point.value));
      const predLog = Math.log10(Math.max(1, predicted));
      diff = obsLog - predLog;
      sigma = sigmaLogPFU;
    }

    sse += diff * diff;
    logLikelihood -= 0.5 * Math.log(2 * Math.PI * sigma * sigma) + (diff * diff) / (2 * sigma * sigma);

    residuals.push({
      timeMin: point.timeMin,
      observed: Math.round(point.value * 100) / 100,
      predicted: Math.round(predicted * 100) / 100,
      residual: Math.round(diff * 1000) / 1000,
    });
  }

  return { sse, logLikelihood, residuals, trajectory };
}

/**
 * Nelder-Mead Simplex Optimization for Non-Linear Least Squares parameter estimation
 */
export function fitInfectionParameters(
  initialGuess: InfectionParameters,
  data: DataPoint[],
  maxIterations: number = 80
): InfectionParameters {
  type SimplexVertex = {
    params: [number, number, number, number];
    score: number;
  };

  const toVec = (p: InfectionParameters): [number, number, number, number] => [
    Math.log10(p.adsorptionRate),
    p.latentPeriod,
    p.burstSize,
    p.bacterialGrowthRate,
  ];

  const fromVec = (v: [number, number, number, number]): InfectionParameters => ({
    ...initialGuess,
    adsorptionRate: Math.pow(10, Math.min(-7, Math.max(-12, v[0]))),
    latentPeriod: Math.min(180, Math.max(5, v[1])),
    burstSize: Math.min(600, Math.max(5, v[2])),
    bacterialGrowthRate: Math.min(0.06, Math.max(0.001, v[3])),
  });

  const cost = (v: [number, number, number, number]): number => {
    const p = fromVec(v);
    const fit = evaluateFit(p, data);
    return fit.sse;
  };

  const baseVec = toVec(initialGuess);
  const n = 4;
  const vertices: SimplexVertex[] = [];

  vertices.push({ params: [...baseVec], score: cost(baseVec) });
  const stepScales = [0.15, 2.0, 10.0, 0.003];

  for (let i = 0; i < n; i++) {
    const pert = [...baseVec] as [number, number, number, number];
    pert[i] += stepScales[i];
    vertices.push({ params: pert, score: cost(pert) });
  }

  const alpha = 1.0;
  const gamma = 2.0;
  const rho = 0.5;
  const sigma = 0.5;

  for (let iter = 0; iter < maxIterations; iter++) {
    vertices.sort((a, b) => a.score - b.score);

    const centroid: [number, number, number, number] = [0, 0, 0, 0];
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        centroid[j] += vertices[i].params[j] / n;
      }
    }

    const worst = vertices[n];
    const secondWorst = vertices[n - 1];
    const best = vertices[0];

    const xr: [number, number, number, number] = [0, 0, 0, 0];
    for (let j = 0; j < n; j++) {
      xr[j] = centroid[j] + alpha * (centroid[j] - worst.params[j]);
    }
    const scoreR = cost(xr);

    if (scoreR < secondWorst.score && scoreR >= best.score) {
      vertices[n] = { params: xr, score: scoreR };
      continue;
    }

    if (scoreR < best.score) {
      const xe: [number, number, number, number] = [0, 0, 0, 0];
      for (let j = 0; j < n; j++) {
        xe[j] = centroid[j] + gamma * (xr[j] - centroid[j]);
      }
      const scoreE = cost(xe);
      vertices[n] = scoreE < scoreR ? { params: xe, score: scoreE } : { params: xr, score: scoreR };
      continue;
    }

    const xc: [number, number, number, number] = [0, 0, 0, 0];
    for (let j = 0; j < n; j++) {
      xc[j] = centroid[j] + rho * (worst.params[j] - centroid[j]);
    }
    const scoreC = cost(xc);
    if (scoreC < worst.score) {
      vertices[n] = { params: xc, score: scoreC };
      continue;
    }

    for (let i = 1; i <= n; i++) {
      for (let j = 0; j < n; j++) {
        vertices[i].params[j] = best.params[j] + sigma * (vertices[i].params[j] - best.params[j]);
      }
      vertices[i].score = cost(vertices[i].params);
    }
  }

  vertices.sort((a, b) => a.score - b.score);
  return fromVec(vertices[0].params);
}

/**
 * Scan phage genome for lysis cassette genes (holin, antiholin, endolysin, spanin)
 */
export function analyzeLysisCassette(phage: PhageFull): LysisCassetteAnalysis {
  const cassetteGenes: LysisCassetteGene[] = [];
  const genes = phage.genes ?? [];

  for (const gene of genes) {
    const text = `${gene.name ?? ''} ${gene.product ?? ''}`.toLowerCase();

    // 1. Antiholin check (takes priority over holin)
    if (
      text.includes('antiholin') ||
      text.includes('s107') ||
      (text.includes('ri') && (text.includes('lysis inhibitor') || text.includes('antiholin')))
    ) {
      cassetteGenes.push({
        role: 'antiholin',
        geneId: gene.id,
        name: gene.name ?? 'antiholin',
        product: gene.product ?? 'Lysis inhibitor / antiholin',
        locusTag: gene.locusTag,
        mechanism: 'Binds and titrates active holins to set clock delay',
        transmembraneHelices: 3,
      });
      continue;
    }

    // 2. Holin check
    if (
      text.includes('holin') ||
      text.includes('pinholin') ||
      text.includes('membrane protein s') ||
      text.includes('t protein') ||
      text.includes('gp38') ||
      (text.includes('membrane') && text.includes('lysis'))
    ) {
      cassetteGenes.push({
        role: 'holin',
        geneId: gene.id,
        name: gene.name ?? 'holin',
        product: gene.product ?? 'Holin membrane pore',
        locusTag: gene.locusTag,
        mechanism: 'Oligomerizes in inner membrane to form micron-scale holes',
        transmembraneHelices: text.includes('pinholin') ? 2 : 3,
      });
      continue;
    }

    // 3. Endolysin check
    if (
      text.includes('endolysin') ||
      text.includes('lysozyme') ||
      text.includes('muramidase') ||
      text.includes('amidase') ||
      text.includes('transglycosylase') ||
      text.includes('peptidoglycan hydrolase') ||
      text.includes('r protein') ||
      (text.includes('gp19') && text.includes('lysis'))
    ) {
      cassetteGenes.push({
        role: 'endolysin',
        geneId: gene.id,
        name: gene.name ?? 'endolysin',
        product: gene.product ?? 'Peptidoglycan muralytic endolysin',
        locusTag: gene.locusTag,
        mechanism: 'Cleaves peptidoglycan cell wall once holin depolarizes membrane',
      });
      continue;
    }

    // 4. Spanin check
    if (
      text.includes('spanin') ||
      text.includes('rz') ||
      text.includes('rz1') ||
      text.includes('u-spanin') ||
      text.includes('outer membrane lipoprotein') && text.includes('lysis')
    ) {
      cassetteGenes.push({
        role: 'spanin',
        geneId: gene.id,
        name: gene.name ?? 'spanin',
        product: gene.product ?? 'Outer membrane disruption spanin complex',
        locusTag: gene.locusTag,
        mechanism: 'Fuses inner and outer bacterial membranes for final burst clearance',
      });
      continue;
    }
  }

  const hasHolin = cassetteGenes.some((g) => g.role === 'holin');
  const hasAntiholin = cassetteGenes.some((g) => g.role === 'antiholin');
  const hasEndolysin = cassetteGenes.some((g) => g.role === 'endolysin');
  const hasSpanin = cassetteGenes.some((g) => g.role === 'spanin');

  // Empirical lysis timing prediction from cassette architecture
  let predictedLysisTimingMin = 40.0;
  const timingModulators: string[] = [];

  const phageName = (phage.name ?? '').toLowerCase();
  if (phageName.includes('t4')) {
    predictedLysisTimingMin = 26.0;
    timingModulators.push('T4 canonical rapid lysis cassette (gp19/t/rI)');
  } else if (phageName.includes('lambda')) {
    predictedLysisTimingMin = 48.0;
    timingModulators.push('Lambda dual-start S105/S107 clock motif (48 min target)');
  } else if (phageName.includes('phix174') || phageName.includes('φx174')) {
    predictedLysisTimingMin = 21.0;
    timingModulators.push('PhiX174 non-peptidoglycan muralytic gene E target (21 min)');
  } else {
    // Scaled based on cassette elements and genome length
    if (hasAntiholin) {
      predictedLysisTimingMin += 12.0;
      timingModulators.push('Antiholin delay timer present (+12 min latency)');
    }
    if (hasSpanin) {
      timingModulators.push('Spanin complex present for rapid outer membrane fusion');
    }
    if (phage.genomeLength && phage.genomeLength > 100000) {
      predictedLysisTimingMin += 5.0; // Larger genomes require longer replication window
      timingModulators.push('Large genome replication requirement (+5 min)');
    }
  }

  const architectureSummary =
    `Cassette contains ${cassetteGenes.length} identified lysis genes: ` +
    `[Holin: ${hasHolin ? 'YES' : 'NONE'}, Antiholin: ${hasAntiholin ? 'YES' : 'NONE'}, ` +
    `Endolysin: ${hasEndolysin ? 'YES' : 'NONE'}, Spanin: ${hasSpanin ? 'YES' : 'NONE'}]. ` +
    `Predicted physiological timing: ${predictedLysisTimingMin.toFixed(1)} minutes.`;

  return {
    genes: cassetteGenes,
    hasHolin,
    hasAntiholin,
    hasEndolysin,
    hasSpanin,
    predictedLysisTimingMin: Math.round(predictedLysisTimingMin * 10) / 10,
    timingModulators,
    architectureSummary,
  };
}

/**
 * Correlate inferred kinetic parameters with genomic cassette architecture
 */
export function correlateGenomicLysis(
  inferredLatentMin: number,
  cassette: LysisCassetteAnalysis
): GenomicCorrelationResult {
  const delta = Math.round((inferredLatentMin - cassette.predictedLysisTimingMin) * 10) / 10;
  const absDelta = Math.abs(delta);

  let concordance: 'high' | 'moderate' | 'divergent' = 'high';
  let correlationScore = 92;

  if (absDelta > 15) {
    concordance = 'divergent';
    correlationScore = Math.max(30, 92 - absDelta * 2.5);
  } else if (absDelta > 7) {
    concordance = 'moderate';
    correlationScore = Math.max(60, 92 - absDelta * 2.0);
  }

  const insights: string[] = [];
  if (concordance === 'high') {
    insights.push(
      `Inferred latent period (${inferredLatentMin.toFixed(1)} min) closely matches genomic cassette prediction (${cassette.predictedLysisTimingMin.toFixed(1)} min).`
    );
  } else if (delta > 0) {
    insights.push(
      `Observed latency is ${delta.toFixed(1)} min longer than genomic baseline. May indicate host metabolic limitation or antiholin repression.`
    );
  } else {
    insights.push(
      `Observed latency is ${Math.abs(delta).toFixed(1)} min earlier than baseline, consistent with high initial MOI or active lysis trigger.`
    );
  }

  if (cassette.hasHolin && cassette.hasAntiholin) {
    insights.push('Dual-component holin-antiholin ratio acts as the primary molecular timer governing the observed rise period.');
  }

  return {
    observedVsPredictedDeltaMin: delta,
    concordance,
    correlationScore: Math.round(correlationScore),
    insights,
  };
}

/**
 * In-silico genetic mutation simulation (e.g. antiholin knockout, delayed lysis)
 */
export function simulateInSilicoCassetteMutations(
  baselineParams: InfectionParameters,
  cassette: LysisCassetteAnalysis
): InSilicoMutationShift[] {
  const scenarios: InSilicoMutationShift[] = [];

  // Scenario 1: Antiholin Knockout
  const ahLatent = Math.max(10, baselineParams.latentPeriod - (cassette.hasAntiholin ? 14 : 10));
  // Earlier lysis cuts off replication premature: burst size drops proportionally
  const ahBurst = Math.round(baselineParams.burstSize * (ahLatent / baselineParams.latentPeriod));
  scenarios.push({
    mutationType: 'antiholin_knockout',
    description: 'Antiholin Knockout (Δantiholin / S107-null)',
    predictedLatentPeriodMin: Math.round(ahLatent * 10) / 10,
    predictedBurstSize: ahBurst,
    latentPeriodDeltaMin: Math.round((ahLatent - baselineParams.latentPeriod) * 10) / 10,
    burstSizeDelta: ahBurst - baselineParams.burstSize,
    mechanisticRationale:
      'Eliminating antiholin removes the molecular brake on holin raft aggregation, causing premature membrane puncture and smaller burst yield.',
  });

  // Scenario 2: Holin Overexpression (Hyper-accumulation)
  const hoLatent = Math.max(12, baselineParams.latentPeriod - 6);
  const hoBurst = Math.round(baselineParams.burstSize * (hoLatent / baselineParams.latentPeriod));
  scenarios.push({
    mutationType: 'holin_overexpression',
    description: 'Stronger Promoter on Holin (Accelerated Raft Assembly)',
    predictedLatentPeriodMin: Math.round(hoLatent * 10) / 10,
    predictedBurstSize: hoBurst,
    latentPeriodDeltaMin: Math.round((hoLatent - baselineParams.latentPeriod) * 10) / 10,
    burstSizeDelta: hoBurst - baselineParams.burstSize,
    mechanisticRationale:
      'Faster critical concentration threshold reached in inner membrane; advances lysis timing by 4-8 min.',
  });

  // Scenario 3: Delayed Lysis (Hyper-yield optimization for manufacturing)
  const dlLatent = baselineParams.latentPeriod + 20;
  const dlBurst = Math.round(baselineParams.burstSize * 1.55);
  scenarios.push({
    mutationType: 'delayed_lysis',
    description: 'Delayed Lysis Allele (Yield Optimization)',
    predictedLatentPeriodMin: Math.round(dlLatent * 10) / 10,
    predictedBurstSize: dlBurst,
    latentPeriodDeltaMin: 20,
    burstSizeDelta: dlBurst - baselineParams.burstSize,
    mechanisticRationale:
      'Extending latent period allows 3-4 additional genome replication rounds, boosting total virion output per host by ~55%.',
  });

  return scenarios;
}

/**
 * Run comprehensive Burst Kinetics & Latency Inference
 */
export function inferBurstKinetics(
  phage: PhageFull,
  growthCurve: ExperimentalGrowthCurve,
  options: { initialParams?: Partial<InfectionParameters>; maxIterations?: number; demonstration?: boolean } = {}
): BurstInferenceResult {
  if (options.demonstration !== true) {
    throw new Error('The sigmoid growth-curve model is an explicit demonstration, not biological parameter inference. Validated experimental inputs and an identifiable forward model are required.');
  }
  const autoGuess = estimateInitialParameters(growthCurve.data, growthCurve.defaultB0, growthCurve.defaultP0);
  const defaultGuess: InfectionParameters = {
    ...autoGuess,
    ...(options.initialParams ?? {}),
  };

  // Perform non-linear optimization
  const fittedParameters = fitInfectionParameters(defaultGuess, growthCurve.data, options.maxIterations ?? 75);
  // PFU and bacterial-count observations do not depend on adsorption in this
  // model. OD includes an adsorption-dependent infected-biomass term, whose
  // biological validity and identifiability have not been established.
  const observesAdsorption = growthCurve.data.some(point => point.type === 'OD');
  if (!observesAdsorption) fittedParameters.adsorptionRate = defaultGuess.adsorptionRate;

  // Evaluate fit quality
  const fit = evaluateFit(fittedParameters, growthCurve.data);

  // Calculate R^2 (coefficient of determination)
  const observedValues = growthCurve.data.map((d) => (d.type === 'OD' ? d.value : Math.log10(Math.max(1, d.value))));
  const meanObs = observedValues.reduce((a, b) => a + b, 0) / Math.max(1, observedValues.length);
  const ssTotal = observedValues.reduce((sum, v) => sum + Math.pow(v - meanObs, 2), 0);
  const fitQualityR2 = ssTotal > 0 ? 1 - fit.sse / ssTotal : null;

  // Count parameters present in the observation equations, not proof of
  // identifiability: latency, PFU burst size, biomass growth, OD adsorption.
  const nPoints = growthCurve.data.length;
  const parameterCount = 1 + Number(growthCurve.data.some(point => point.type === 'PFU')) +
    Number(growthCurve.data.some(point => point.type !== 'PFU')) + Number(observesAdsorption);
  const aic = Math.round((2 * parameterCount - 2 * fit.logLikelihood) * 10) / 10;
  const bic = Math.round((parameterCount * Math.log(Math.max(1, nPoints)) - 2 * fit.logLikelihood) * 10) / 10;

  // Lysis cassette analysis and genomic correlation
  const lysisCassette = analyzeLysisCassette(phage);
  const genomicCorrelation = correlateGenomicLysis(fittedParameters.latentPeriod, lysisCassette);
  const inSilicoScenarios = simulateInSilicoCassetteMutations(fittedParameters, lysisCassette);

  const summary =
    `Demonstration curve ${growthCurve.title}, using ${phage.name} annotations for illustrative cassette scenarios. ` +
    `Sigmoid model latency = ${fittedParameters.latentPeriod.toFixed(1)} min; model burst size = ${Math.round(fittedParameters.burstSize)}. ` +
    `${observesAdsorption ? 'The OD adsorption fit is unvalidated' : 'Adsorption is not identifiable from these observations'}; uncertainty has not been estimated. ` +
    `Descriptive R² = ${fitQualityR2?.toFixed(3) ?? 'undefined for constant observations'}.`;

  return {
    source: 'demonstration',
    assumptions: 'Hand-entered example growth curves without verified experimental provenance, fitted to a sigmoid approximation rather than a DDE. PFU and bacterial-count curves do not depend on adsorption; OD uses an unvalidated infected-biomass term. No bootstrap or confidence interval was computed. Cassette timing and mutation scenarios are illustrative, not predictions for the selected phage.',
    adsorptionRateStatus: observesAdsorption ? 'unvalidated_fit' : 'unidentifiable',
    curveId: growthCurve.id,
    curveTitle: growthCurve.title,
    phageName: phage.name,
    fittedParameters,
    confidenceIntervals: null,
    fitQualityR2: fitQualityR2 === null ? null : Math.round(fitQualityR2 * 1000) / 1000,
    logLikelihood: Math.round(fit.logLikelihood * 10) / 10,
    aic,
    bic,
    residuals: fit.residuals,
    fittedTrajectory: fit.trajectory,
    lysisCassette,
    genomicCorrelation,
    inSilicoScenarios,
    summary,
  };
}
